import React, { forwardRef, type Ref } from 'react';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import IconButton, { type IconButtonProps } from '@atlaskit/button/icon/button';
import { token } from '@atlaskit/tokens';
import { useIntl, defineMessage } from '@atlassian/jira-intl';

const projectActionsMenu = defineMessage({
	id: 'navigation-project-action-menu.menu-container.project-actions-menu',
	defaultMessage: 'More actions',
	description:
		'Used as the alt text for an icon button that when used shows a popup menu of more options.',
});

const projectActionsMenuName = defineMessage({
	id: 'navigation-project-action-menu.menu-container.project-actions-menu-name',
	defaultMessage: 'More actions for {name}',
	description: '{name} will be replaced with projects side menu and sub projects name.',
});

type MenuTriggerButtonProps = Omit<IconButtonProps, 'spacing' | 'icon' | 'label'> & {
	actionMenuId?: string;
};

export const MenuTriggerButton: React.ForwardRefExoticComponent<
	MenuTriggerButtonProps & React.RefAttributes<HTMLButtonElement>
> = forwardRef(
	(
		{ onClick, name, actionMenuId, ...props }: MenuTriggerButtonProps,
		ref: Ref<HTMLButtonElement>,
	) => {
		const { formatMessage } = useIntl();

		return (
			<IconButton
				{...props}
				icon={(iconProps) => (
					<ShowMoreHorizontalIcon {...iconProps} color={token('color.icon')} size="small" />
				)}
				label={formatMessage(projectActionsMenuName, { name })}
				onClick={onClick}
				ref={ref}
				spacing="compact"
				appearance="subtle"
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				testId="navigation-project-action-menu.ui.menu-container.themed-button"
				id={actionMenuId}
				isTooltipDisabled={false}
				tooltip={{
					content: formatMessage(projectActionsMenu),
					hideTooltipOnClick: true,
				}}
			/>
		);
	},
);
